import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { ConfidenceMeter } from '@/components/ui/ConfidenceMeter'
import { EnsembleLoader } from '@/components/ui/LoadingState'
import { formatPercent, formatWilson } from '@/lib/utils'
import { runDemoEnsemble } from '@/lib/ensemble/demoRunner'
import { DEMO_PRE_READ_QUESTION } from '@/lib/demo/data'
import type { ValidationCell, SegmentAggregate } from '@/lib/types'
import { buildValidationPlan, simulateValidation } from '@/lib/ensemble/validation'

type Phase = 'loading' | 'empty' | 'plan' | 'simulating' | 'results'

interface PreRead {
  pre_read_id: string
  research_question: string
  stimuli: string   // JSON
  result: string    // JSON array of SegmentAggregate
  simulated_only: number
  created_at: number
}

export function ValidationPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const isDemo = !projectId || projectId === 'demo'

  const [phase, setPhase] = useState<Phase>(isDemo ? 'plan' : 'loading')
  const [loaderStep, setLoaderStep] = useState(0)
  const [cells, setCells] = useState<ValidationCell[]>([])
  const [validated, setValidated] = useState<ValidationCell[]>([])
  const [latestPreRead, setLatestPreRead] = useState<PreRead | null>(null)
  const [preReadAggregates, setPreReadAggregates] = useState<SegmentAggregate[]>([])

  // For real projects: fetch latest pre-read to build a real validation plan
  useEffect(() => {
    if (isDemo) return
    const token = localStorage.getItem('echo_session')
    if (!token) { setPhase('empty'); return }

    fetch(`/api/projects/${projectId}/pre-reads`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<PreRead[]> : [])
      .then(preReads => {
        const latest = (preReads ?? [])[0] ?? null
        if (!latest) { setPhase('empty'); return }

        setLatestPreRead(latest)
        let aggregates: SegmentAggregate[] = []
        try { aggregates = JSON.parse(latest.result) as SegmentAggregate[] } catch { }
        setPreReadAggregates(aggregates)

        if (aggregates.length > 0) {
          const plan = buildValidationPlan(aggregates)
          setCells(plan)
          setPhase('plan')
        } else {
          setPhase('empty')
        }
      })
      .catch(() => setPhase('empty'))
  }, [projectId, isDemo])

  const loadDemoPlan = async () => {
    setPhase('simulating')
    setLoaderStep(0)
    for (let i = 1; i <= 4; i++) {
      await new Promise(r => setTimeout(r, 500))
      setLoaderStep(i)
    }
    const result = await runDemoEnsemble(DEMO_PRE_READ_QUESTION, [])
    const plan = buildValidationPlan(result.aggregates)
    const sim = simulateValidation(plan)
    setCells(plan)
    setValidated(sim)
    setPhase('results')
  }

  const runSimulatedValidation = async () => {
    setPhase('simulating')
    setLoaderStep(0)
    for (let i = 1; i <= 4; i++) {
      await new Promise(r => setTimeout(r, 500))
      setLoaderStep(i)
    }
    const sim = simulateValidation(cells)
    setValidated(sim)
    setPhase('results')
  }

  const reportPath = `/app/projects/${isDemo ? 'demo' : projectId}/report`
  const preReadPath = `/app/projects/${isDemo ? 'demo' : projectId}/pre-read`

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <div className="p-6 max-w-xl mx-auto pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink mb-6" style={{ letterSpacing: '-0.02em' }}>Human Validation</h1>
        <Card className="text-center py-10">
          <p className="text-sm text-slate-400">Loading pre-read results…</p>
        </Card>
      </div>
    )
  }

  // ── Empty — no pre-reads yet ─────────────────────────────────────────────────
  if (phase === 'empty') {
    return (
      <div className="p-6 space-y-6 max-w-3xl pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Human Validation Planner</h1>
        <Card className="text-center py-10 space-y-3">
          <p className="text-sm font-medium text-deep-ink">No pre-read results yet</p>
          <p className="text-xs text-slate-500 max-w-xs mx-auto">
            Run an ensemble pre-read first. The validation plan is generated automatically from its confidence scores.
          </p>
          <div className="flex gap-3 justify-center pt-2">
            <Link to={preReadPath}>
              <Button variant="primary" size="sm">Run a pre-read →</Button>
            </Link>
          </div>
        </Card>
      </div>
    )
  }

  // ── Simulating ───────────────────────────────────────────────────────────────
  if (phase === 'simulating') {
    return (
      <div className="p-6 max-w-xl mx-auto pb-20 md:pb-6">
        <h1 className="text-2xl font-medium text-deep-ink mb-6">Simulating validation…</h1>
        <Card><EnsembleLoader step={loaderStep} total={5} /></Card>
      </div>
    )
  }

  // ── Results ──────────────────────────────────────────────────────────────────
  if (phase === 'results' && validated.length > 0) {
    return (
      <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Human Validation</h1>
            <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
              <Badge variant="demo" size="sm">Simulated</Badge>
              {validated.length} cells validated
            </p>
          </div>
          <Link to={reportPath}>
            <Button variant="primary" size="sm">Generate report →</Button>
          </Link>
        </div>

        {isDemo && (
          <Card className="border-human-teal/30 bg-human-teal/5">
            <p className="text-sm font-medium text-deep-ink mb-1">Key finding: Echo was right overall, but missed a segment</p>
            <p className="text-sm text-slate-600">
              The ensemble correctly predicted Message B wins for General Homeowners and Price-Sensitive Renters.
              Human validation confirmed this — but revealed that Recent Claimants need explicit claims-reassurance
              language that the echo missed entirely.
            </p>
            <p className="text-xs text-slate-400 mt-2 italic">This is the value of the validation loop: the echo gave you a fast directional read; human validation found the risky segment.</p>
          </Card>
        )}

        <div className="space-y-4">
          {validated.map((cell, i) => {
            const agreementColor = (cell.agreement_score ?? 0) >= 0.75 ? 'text-validation-green' : (cell.agreement_score ?? 0) >= 0.55 ? 'text-uncertainty-amber' : 'text-risk-rose'

            return (
              <Card key={i} className="overflow-hidden">
                <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                  <div>
                    <p className="text-sm font-medium text-deep-ink">{cell.segment_name} · {cell.stimulus_label}</p>
                    <p className="text-xs text-slate-400">{cell.sample_size_needed} respondents</p>
                  </div>
                  <div className={`text-lg font-medium ${agreementColor}`}>
                    {formatPercent(cell.agreement_score ?? 0)} agreement
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div className="bg-soft-panel rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Echo Prediction</p>
                    <p className="text-sm font-medium text-deep-ink">{cell.echo_prediction}</p>
                    <ConfidenceMeter confidence={cell.echo_confidence} groundTruthPresent={false} className="mt-2" />
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-validation-green/20">
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">Human Validation</p>
                    <p className="text-sm font-medium text-deep-ink">{cell.human_result}</p>
                    {cell.updated_confidence && (
                      <ConfidenceMeter confidence={cell.updated_confidence} groundTruthPresent={true} className="mt-2" />
                    )}
                  </div>
                </div>

                {cell.lessons && cell.lessons.length > 0 && (
                  <div className="rounded-lg p-3 border bg-green-50 border-validation-green/30">
                    <p className="text-xs font-medium mb-2 text-validation-green">Lessons</p>
                    {cell.lessons.map((lesson, j) => (
                      <p key={j} className="text-xs text-slate-600 flex items-start gap-1.5">
                        <span className="shrink-0 mt-0.5">✓</span>{lesson}
                      </p>
                    ))}
                  </div>
                )}
              </Card>
            )
          })}
        </div>

        <Card className="bg-off-white border-0">
          <h3 className="text-sm font-medium text-deep-ink mb-2">Calibration note</h3>
          <p className="text-sm text-slate-600">
            This validation round has been recorded in the calibration ledger. Pass weights will update based on agreement scores for future runs on similar questions.
          </p>
        </Card>
      </div>
    )
  }

  // ── Validation Plan ──────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 max-w-3xl pb-20 md:pb-6">
      <h1 className="text-2xl font-medium text-deep-ink" style={{ letterSpacing: '-0.02em' }}>Human Validation Planner</h1>
      <p className="text-sm text-slate-500">
        {isDemo
          ? 'Based on the ensemble pre-read, Echosome has identified which cells need real-human validation.'
          : 'Cells flagged for validation based on the most recent pre-read\'s confidence scores.'}
      </p>

      {/* Research question context for real projects */}
      {!isDemo && latestPreRead && (
        <Card className="bg-off-white border-0 space-y-1">
          <p className="text-xs text-slate-400">Pre-read question</p>
          <p className="text-sm font-medium text-deep-ink">"{latestPreRead.research_question}"</p>
          <p className="text-xs text-slate-400 mt-1">
            {latestPreRead.simulated_only ? 'Simulated run' : 'Live LLM run'} · {new Date(latestPreRead.created_at).toLocaleDateString()}
          </p>
          <div className="pt-1">
            <Link to={preReadPath} className="text-xs text-human-teal hover:underline">Run another pre-read →</Link>
          </div>
        </Card>
      )}

      {/* Validation plan cells */}
      {cells.length > 0 ? (
        <Card className="space-y-4">
          <h3 className="text-sm font-medium text-deep-ink">Recommended validation cells</h3>
          <div className="space-y-3">
            {cells.map((cell, i) => (
              <div key={i} className="bg-soft-panel rounded-lg p-4 border border-border-gray">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-sm font-medium text-deep-ink">{cell.segment_name} · {cell.stimulus_label}</p>
                  <Badge variant={cell.echo_confidence.band === 'Low' ? 'danger' : 'warning'} size="sm">
                    {cell.sample_size_needed} respondents
                  </Badge>
                </div>
                <p className="text-xs text-slate-600">{cell.reason}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Echo prediction: <span className="font-medium text-deep-ink">{cell.echo_prediction}</span>
                  {' · '}Wilson: {formatWilson(cell.echo_confidence.wilson)}
                </p>
              </div>
            ))}
          </div>

          <div className="pt-3 border-t border-border-gray">
            <p className="text-sm text-slate-600">
              <strong className="text-deep-ink">Total:</strong> {cells.reduce((s, c) => s + c.sample_size_needed, 0)} respondents across {cells.length} cells.
            </p>
          </div>
        </Card>
      ) : isDemo ? (
        /* Demo fallback hardcoded plan */
        <Card className="space-y-4">
          <h3 className="text-sm font-medium text-deep-ink">Recommended validation cells</h3>
          <div className="space-y-3">
            {[
              {
                segment: 'Recent Claimants', stimulus: 'Message B', n: 20,
                reason: 'Low confidence (Wilson 32–58%). Only 12 relevant open-end responses. Echo confidence is Low for this segment.',
                priority: 'high',
              },
              {
                segment: 'General Homeowners', stimulus: 'Message B', n: 40,
                reason: 'Medium-high confidence but high stakes. Validation will narrow Wilson interval from 72–86% to 78–84%.',
                priority: 'medium',
              },
            ].map((cell, i) => (
              <div key={i} className="bg-soft-panel rounded-lg p-4 border border-border-gray">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <p className="text-sm font-medium text-deep-ink">{cell.segment} · {cell.stimulus}</p>
                  <Badge variant={cell.priority === 'high' ? 'danger' : 'warning'} size="sm">{cell.n} respondents</Badge>
                </div>
                <p className="text-xs text-slate-600">{cell.reason}</p>
              </div>
            ))}
          </div>
          <div className="pt-3 border-t border-border-gray">
            <p className="text-sm text-slate-600"><strong className="text-deep-ink">Total:</strong> 60 respondents across 2 cells.</p>
          </div>
        </Card>
      ) : null}

      {/* Generated validation questions — demo only */}
      {isDemo && (
        <Card className="space-y-3">
          <h3 className="text-sm font-medium text-deep-ink">Generated validation questions</h3>
          <p className="text-xs text-slate-500">Auto-generated from the pre-read uncertainty analysis. Edit before fielding.</p>
          {[
            { cell: 'Recent Claimants', q: 'After your recent claim experience, which renewal notice would make you feel most understood by your insurer?' },
            { cell: 'General Homeowners', q: 'Which of these renewal messages gives you the clearest understanding of why your premium changed?' },
          ].map((item, i) => (
            <div key={i} className="border border-border-gray rounded-lg p-3 space-y-1">
              <p className="text-xs text-slate-400">{item.cell}</p>
              <p className="text-sm text-slate-700">{item.q}</p>
            </div>
          ))}
        </Card>
      )}

      <Button
        variant="primary"
        size="lg"
        className="w-full"
        onClick={isDemo ? loadDemoPlan : (cells.length > 0 ? runSimulatedValidation : undefined)}
        disabled={!isDemo && cells.length === 0}
      >
        {isDemo ? 'Run simulated validation →' : (cells.length > 0 ? 'Simulate validation →' : 'Run a pre-read first')}
      </Button>
    </div>
  )
}
